import React, { useState } from 'react';
import API_URL from '../config/api';
import emailjs from '@emailjs/browser';
import './PlanTrip.css';

export default function PlanTrip({ onClose }) {
  const [formData, setFormData] = useState({
    destination: '',
    adults: 2,
    children: 0,
    month: 'Any month',
    year: 'Any year',
    budget: '',
    firstName: '',
    lastName: '',
    countryCode: '+48',
    phone: '',
    email: '',
    availability: 'Any time'
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGuestChange = (type, delta) => {
    setFormData(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + delta)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send email via EmailJS
      try {
        const emailMessage = `
Plan Trip Inquiry Details:
- Destination: ${formData.destination}
- Guests: ${formData.adults} adults, ${formData.children} children
- Travel Date: ${formData.month} ${formData.year}
- Budget: ${formData.budget}
- Name: ${formData.firstName} ${formData.lastName}
- Phone: ${formData.countryCode} ${formData.phone}
- Email: ${formData.email}
- Availability: ${formData.availability}
        `.trim();

        await emailjs.send(
          'service_r8rvio8',
          'template_akg3o96',
          {
            from_name: `${formData.firstName} ${formData.lastName}`,
            from_email: formData.email,
            message: emailMessage,
            to_email: 'contact@sefarium.com',
          }
        );
        console.log('Email sent successfully via EmailJS');
      } catch (emailError) {
        console.error('EmailJS error:', emailError);
      }

      // Save to database (backend)
      const res = await fetch(`${API_URL}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          type: 'plan_trip',
          name: `${formData.firstName} ${formData.lastName}`,
        }),
        mode: 'cors'
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Server error' }));
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      if (data.success === true) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          if (onClose) onClose();
        }, 3000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      alert('Failed to submit enquiry. Please try again.');
    }
  };

  const destinations = [
    'Vienna, Austria',
    'Krakow, Poland',
    'Warsaw, Poland',
    'Budapest, Hungary',
    'Hallstatt, Austria',
    'Paris, France',
    'Rome, Italy',
    'Zakopane, Poland',
    'Other'
  ];

  const months = [
    'Any month',
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = ['Any year', '2026', '2027', '2028', '2029', '2030'];

  const budgetOptions = [
    'Looking for guidance',
    'Under €2,000',
    '€2,000 - €5,000',
    '€5,000 - €10,000',
    '€10,000 - €20,000',
    'Over €20,000'
  ];

  const availabilityOptions = [
    'Any time',
    'Weekdays only',
    'Weekends only',
    'Morning',
    'Afternoon',
    'Evening'
  ];

  const countryCodes = ['+48', '+1', '+44', '+33', '+39', '+43', '+36'];

  if (submitted) {
    return (
      <div className="plan-trip-overlay" onClick={onClose}>
        <div className="plan-trip-modal" onClick={(e) => e.stopPropagation()}>
          <div className="plan-trip-success">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>We've received your enquiry and will contact you soon.</p>
            <button className="plan-trip-close" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="plan-trip-overlay" onClick={onClose}>
      <div className="plan-trip-modal" onClick={(e) => e.stopPropagation()}>
        <div className="plan-trip-header">
          <h2>Plan Your Trip</h2>
          <button className="plan-trip-close-btn" onClick={onClose}>×</button>
        </div>
        
        <form className="plan-trip-form" onSubmit={handleSubmit}>
          <div className="plan-trip-section">
            <div className="plan-trip-field">
              <label>Where would you like to go?</label>
              <select name="destination" value={formData.destination} onChange={handleChange}>
                <option value="">Select destination</option>
                {destinations.map(dest => (
                  <option key={dest} value={dest}>{dest}</option>
                ))}
              </select>
            </div>

            <div className="plan-trip-field">
              <label>Guests</label>
              <div className="guests-input">
                <div className="guest-counter">
                  <label>Adults (age 15+)</label>
                  <div className="counter-controls">
                    <button type="button" onClick={() => handleGuestChange('adults', -1)}>-</button>
                    <input type="number" value={formData.adults} readOnly min="0" />
                    <button type="button" onClick={() => handleGuestChange('adults', 1)}>+</button>
                  </div>
                </div>
                <div className="guest-counter">
                  <label>Children (age 0-15)</label>
                  <div className="counter-controls">
                    <button type="button" onClick={() => handleGuestChange('children', -1)}>-</button>
                    <input type="number" value={formData.children} readOnly min="0" />
                    <button type="button" onClick={() => handleGuestChange('children', 1)}>+</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="plan-trip-field">
              <label>When would you like to go?</label>
              <div className="date-inputs">
                <select name="month" value={formData.month} onChange={handleChange}>
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <select name="year" value={formData.year} onChange={handleChange}>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="plan-trip-field">
              <label>How much are you looking to spend? *</label>
              <select name="budget" value={formData.budget} onChange={handleChange} required>
                <option value="">Select budget</option>
                {budgetOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="plan-trip-section">
            <h3 className="section-title">Your Details</h3>
            
            <div className="plan-trip-field">
              <label>First name *</label>
              <input 
                type="text" 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="plan-trip-field">
              <label>Last name *</label>
              <input 
                type="text" 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="plan-trip-field">
              <label>Phone number *</label>
              <div className="phone-input">
                <select name="countryCode" value={formData.countryCode} onChange={handleChange}>
                  {countryCodes.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  placeholder="Phone number *"
                  required 
                />
              </div>
            </div>

            <div className="plan-trip-field">
              <label>Email address *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                placeholder="Email address *"
                required 
              />
            </div>

            <div className="plan-trip-field">
              <label>When are you available?</label>
              <select name="availability" value={formData.availability} onChange={handleChange}>
                {availabilityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="plan-trip-actions">
            <button type="button" className="plan-trip-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="plan-trip-submit">
              Submit Enquiry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

